import * as React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

// helpers
import { textResources } from 'src/lang/de'
import { IPlaceSuggestion, PlaceSuggestionType, characterCount } from 'src/utils/places/autocomplete'
import { usePlaceSuggestions } from 'src/utils/places/use_place_suggestions'

// constants
import { CLASS_PREFIX } from 'src/constants'
import {
  LOCATION_PICKER_ATTRIBUTION,
  LOCATION_PICKER_MIN_QUERY_LENGTH
} from 'src/constants/location_picker'

// components
import InputField, { InputFieldHandle } from 'src/components/forms/input_field/input_field'
import LocationPickerItemSegment from 'src/components/item_segment/layouts/location_picker/location_picker'

// assets
import './place_search.scss'

interface IProps {
  autoFocus?: boolean; // default: true
  onChangeQuery: (query: string) => void;
  onFocus: () => void;
  onSelect: (suggestion: IPlaceSuggestion) => void;
  onHasResults: (value: boolean) => void;
  area?: string;
  type?: PlaceSuggestionType;
  clear?: () => void;
  close?: () => void;
  query: string;
  errorText?: string;
  floatLabel?: boolean;
  label?: string;
  placeholder?: string;
  valid?: boolean;
  noBackground: boolean;
  overlayResults?: boolean;
}

const cls: string = CLASS_PREFIX + 'location-picker'
const resultsListClass: string = `${cls}__results`
const pickerHeaderClass: string = `${cls}__header`
const labels = textResources.locationChanger

const PlaceSearch: React.FC<IProps> = ({
  autoFocus = true,
  onChangeQuery,
  onFocus,
  onSelect,
  onHasResults,
  area,
  type,
  clear,
  close,
  query,
  errorText,
  floatLabel,
  label,
  placeholder,
  valid = true,
  noBackground,
  overlayResults
}) => {
  const inputRef = useRef<InputFieldHandle>(null)
  const [showResult, setShowResult] = useState(false)
  const { cancel, reset, search, searchNow, state } = usePlaceSuggestions({ area, type })

  const hasRows = state.suggestions.length > 0
  const isLongEnough = characterCount(query) >= LOCATION_PICKER_MIN_QUERY_LENGTH
  const hadRows = useRef(false)
  const typedQuery = useRef(query)

  // The picker sets the field itself ("Meine Adresse", a located address, a prefilled one):
  // suggestions, a failure or a retry for the text typed before no longer belong to it.
  useEffect(() => {
    if (query !== typedQuery.current) {
      typedQuery.current = query
      // the rows go without telling the parent: it closed its action list when it set the field
      hadRows.current = false
      reset()
    }
  }, [query, reset])

  useEffect(() => {
    if (hadRows.current !== hasRows) {
      hadRows.current = hasRows
      onHasResults(hasRows)
    }
  }, [hasRows])

  const handleInputChange = useCallback((value: string) => {
    setShowResult(true)
    typedQuery.current = value
    onChangeQuery(value)
    if (characterCount(value) < LOCATION_PICKER_MIN_QUERY_LENGTH) {
      onHasResults(false)
    }
    search(value)
  }, [onChangeQuery, search])

  const handlePick = useCallback((suggestion: IPlaceSuggestion) => () => {
    cancel()
    // the parent puts the picked text into the field; that is the suggestion's own, not a foreign one
    typedQuery.current = suggestion.text
    onSelect(suggestion)
    setShowResult(false)
  }, [onSelect, cancel])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      searchNow()
    }
  }, [searchNow])

  const handleClear = useCallback(() => {
    typedQuery.current = ''
    onChangeQuery('')
    inputRef.current?.focusInput()
    reset()
    clear?.()
  }, [onChangeQuery, reset, clear])

  const handleRightIcon = useCallback(() => {
    if (query.length) {
      handleClear()
    } else {
      close?.()
    }
  }, [query, handleClear, close])

  const assistiveText = !isLongEnough && query.length !== 0
    ? textResources.shared.inputTooShort(LOCATION_PICKER_MIN_QUERY_LENGTH)
    : ''

  const showSlowText = state.slow && state.status === 'loading'

  const renderBody = () => {
    if (state.status === 'error') {
      return (
        <p className={`${cls}__status ${cls}__status--error`} role='alert'>
          {state.error === 'offline' ? labels.searchOffline : labels.searchError}
        </p>
      )
    }
    if (state.status === 'ready' && !hasRows) {
      return <p className={`${cls}__noLocationFound`}>{textResources.shared.noResultFound}</p>
    }
    return state.suggestions.map((suggestion) => (
      <LocationPickerItemSegment
        key={suggestion.placeId}
        label={suggestion.mainText}
        description={suggestion.secondaryText}
        emphasizeLabel
        navigation={{ onClick: handlePick(suggestion) }}
      />
    ))
  }

  const renderResult = () => {
    const waitingForFirstAnswer = state.status === 'loading' && !state.answered && !state.slow
    if (!query.length || state.status === 'idle' || waitingForFirstAnswer) {
      return null
    }

    return (
      <span className={showResult ? resultsListClass : `${resultsListClass}--hide`}>
        <div className={pickerHeaderClass}>
          <div className={`${pickerHeaderClass}__top`}>
            <p
              className={`${cls}__status ${pickerHeaderClass}__loading${state.slow ? '' : ` ${pickerHeaderClass}__loading--hidden`}`}
              aria-hidden='true'
            >
              <span className={`${cls}__status-spinner`} />
              {labels.searchLoading}
            </p>
            <span className={`${pickerHeaderClass}__attribution`}>{LOCATION_PICKER_ATTRIBUTION}</span>
          </div>
          <h2 className={`${pickerHeaderClass}__title`}>{labels.searchResults}</h2>
        </div>
        {renderBody()}
      </span>
    )
  }

  const input = (
    <div className={`${cls}__search`}>
      <InputField
        assistiveText={assistiveText}
        autoComplete={'off'}
        spellCheck={false}
        autoCorrect='off'
        floatLabel={floatLabel}
        label={label}
        placeholder={placeholder || labels.inputPlaceholder}
        name='location_search'
        iconLeft={'nav-pin-filled'}
        iconRight={query.length > 0 ? 'x-circle' : undefined}
        autoFocus={autoFocus}
        type='search'
        onRightIconClick={handleRightIcon}
        onChange={handleInputChange}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        value={query}
        ref={inputRef}
        valid={valid}
        errorText={errorText}
        noBackground={noBackground}
      />
      {/* a live region that is always mounted, so a slow search is announced when its text arrives */}
      <span className={`${cls}__live-status`} role='status'>{showSlowText ? labels.searchLoading : ''}</span>
    </div>
  )

  if (overlayResults) {
    return (
      <div className={`${cls}--overlay-container`}>
        {input}
        {renderResult()}
      </div>
    )
  }

  return (
    <>
      {input}
      {renderResult()}
    </>
  )
}

export default PlaceSearch
